import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../../api/client.js";

/**
 * DR -> RE auto-production (owner t804u), owner-only: the Deep Research tab's "Auto-produce top 3 (RE)" for today's ranking of the default bank.
 * Self-contained here (FIX-062 isolation: no import of an RE module); it only calls the owner-only `/re/:tenantId/auto/...` API.
 * Safety net (O2): locks on click, a confirm dialog with the picks and the estimate, one run at a time, max 3 button runs a day, produced reels never run (§15).
 */
interface Pick { readonly slot: number; readonly code: string; readonly num: number; readonly lead: "EMMA" | "YUNA_1"; readonly tool: string | null; readonly score: number | null }
interface DrPreview { readonly picks: readonly Pick[]; readonly videos: number; readonly estimate: { readonly text: string }; readonly dialog: string; readonly day: string | null; readonly pending: number }
interface RePreview { readonly produced: { readonly message: string } | null; readonly tooLong: boolean; readonly videos: number; readonly estimate: { readonly text: string } }
interface Status { readonly enabled: boolean; readonly activeRun: { readonly id: string } | null; readonly buttonRuns: { readonly used: number; readonly max: number }; readonly settings: { readonly paused: boolean; readonly pausedReason: string | null }; readonly personas: { readonly EMMA: string; readonly YUNA_1: string } }
interface RunView { readonly run: { readonly id: string; readonly status: string; readonly error: string | null; readonly targetVideos: number }; readonly report: { readonly line?: string } & Record<string, unknown> }

const bodyMessage = (e: unknown, fallback: string): string => {
  const b = e instanceof ApiError ? (e.body as { message?: string; error?: string } | null) : null;
  return b?.message ?? b?.error ?? fallback;
};

function useAutoStatus(tenantId: string): { status: Status | null; hidden: boolean; reload: () => void } {
  const [status, setStatus] = useState<Status | null>(null);
  const [hidden, setHidden] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    api.get<Status>(`/re/${tenantId}/auto/status`).then((s) => { if (live) setStatus(s); }, (e: unknown) => { if (live && e instanceof ApiError && (e.status === 403 || e.status === 503 || e.status === 404)) setHidden(true); });
    return () => { live = false; };
  }, [tenantId, tick]);
  return { status, hidden, reload: () => setTick((x) => x + 1) };
}

export function DrAutoRunCard({ tenantId, runId, csrfToken, onEnd }: { readonly tenantId: string; readonly runId: string; readonly csrfToken: string | null; readonly onEnd?: () => void }) {
  const [view, setView] = useState<RunView | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const ended = useRef(false);
  useEffect(() => {
    let live = true;
    const load = (): void => {
      api.get<RunView>(`/re/${tenantId}/auto/runs/${encodeURIComponent(runId)}`).then((v) => {
        if (!live) return; setView(v);
        if (v.run.status !== "running" && !ended.current) { ended.current = true; onEnd?.(); }
      }).catch(() => undefined);
    };
    load();
    const t = setInterval(load, 15_000);
    return () => { live = false; clearInterval(t); };
  }, [tenantId, runId]);
  const stop = async (): Promise<void> => {
    if (!window.confirm("Stop this run? No new step or paid call starts; videos already submitted stay in the normal pipeline.")) return;
    try { await api.post(`/re/${tenantId}/auto/runs/${encodeURIComponent(runId)}/stop`, csrfToken, {}); setMsg("Stopped."); } catch (e) { setMsg(bodyMessage(e, "Stop failed.")); }
  };
  return (
    <div className="card" data-testid="re-auto-run">
      <strong>Auto-production run</strong> <span className="muted">{runId}</span>
      <p data-testid="re-auto-run-status">Status: {view?.run.status ?? "…"}{view?.run.error ? ` (${view.run.error})` : ""}</p>
      {view?.report.line ? <p data-testid="re-auto-run-summary">{String(view.report.line)}</p> : null}
      {view?.run.status === "running" ? <button type="button" data-testid="re-auto-stop" onClick={() => void stop()}>Stop</button> : null}
      {msg ? <p className="muted">{msg}</p> : null}
    </div>
  );
}

/** FIX-079 (owner t1528u): owner-only Unpause on a paused card. EXISTING endpoint POST /re/:t/auto/pause {paused:false}, owner session + CSRF; confirm first; refresh after. */
export function DrAutoUnpause({ tenantId, csrfToken, status, onDone }: { readonly tenantId: string; readonly csrfToken: string | null; readonly status: Status; readonly onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  if (!status.enabled || !status.settings.paused) return null;
  const resume = async (): Promise<void> => {
    const lines = [`Unpause RE auto-production?${status.settings.pausedReason ? `\nPaused because: ${status.settings.pausedReason}` : ""}`];
    if (status.activeRun) lines.push(`Warning: run ${status.activeRun.id} is still running - its paid steps will continue once unpaused.`);
    lines.push("If the cause (e.g. WaveSpeed credit) is not fixed, it may pause again.");
    if (!window.confirm(lines.join("\n"))) return;
    setBusy(true); setMsg(null);
    try { await api.post(`/re/${tenantId}/auto/pause`, csrfToken, { paused: false }); setMsg("Unpaused."); onDone(); }
    catch (e) { setMsg(bodyMessage(e, "Could not unpause.")); }
    finally { setBusy(false); }
  };
  return (
    <span data-testid="re-auto-unpause-wrap">
      {" "}<button type="button" data-testid="re-auto-unpause" disabled={busy} onClick={() => void resume()}>Unpause</button>
      {msg ? <span className="muted" data-testid="re-auto-unpause-msg"> {msg}</span> : null}
    </span>
  );
}

export function DrAutoProduceButton({ tenantId, csrfToken }: { readonly tenantId: string; readonly csrfToken: string | null }) {
  const { status, hidden, reload } = useAutoStatus(tenantId);
  const [busy, setBusy] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  if (hidden || status === null) return null;
  const active = runId ?? status.activeRun?.id ?? null;
  const off = !status.enabled ? "Auto-production is switched off." : status.settings.paused ? `Paused${status.settings.pausedReason ? `: ${status.settings.pausedReason}` : ""}.` : status.buttonRuns.used >= status.buttonRuns.max ? `The buttons ran ${status.buttonRuns.used} times today (max ${status.buttonRuns.max}).` : null;
  const go = async (): Promise<void> => {
    setBusy(true); setMsg(null); // locked from the first click
    try {
      const pv = await api.get<DrPreview>(`/re/${tenantId}/auto/preview?kind=dr`);
      if (!pv.picks.length) { setMsg("No eligible reel in today's ranking."); return; }
      if (!window.confirm(pv.dialog)) return;
      const out = await api.post<{ run: { id: string } }>(`/re/${tenantId}/auto/runs`, csrfToken, { trigger: "button_dr" });
      setRunId(out.run.id);
    } catch (e) { setMsg(bodyMessage(e, "Could not start the run.")); }
    finally { setBusy(false); reload(); }
  };
  return (
    <div data-testid="re-auto-dr">
      <button type="button" className="auto-produce" data-testid="re-auto-dr-button" disabled={busy || active !== null || off !== null} title={off ?? undefined} onClick={() => void go()}>Auto-produce top 3 (RE)</button>
      {off ? <span className="muted"> {off}</span> : null}
      <DrAutoUnpause tenantId={tenantId} csrfToken={csrfToken} status={status} onDone={reload} />
      {msg ? <p className="error-banner" role="alert">{msg}</p> : null}
      {active ? <DrAutoRunCard tenantId={tenantId} runId={active} csrfToken={csrfToken} onEnd={reload} /> : null}
    </div>
  );
}

